import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="container-app py-10 md:py-14 max-w-3xl" data-testid="privacy-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-orange-600 mb-6"
        data-testid="privacy-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back home
      </Link>

      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-5 h-5 text-orange-600" />
        <span className="text-orange-600 font-medium text-xs tracking-wider uppercase">Privacy</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2">
        Terms of Use &amp; Privacy Policy
      </h1>
      <p className="text-stone-500 mb-10">
        Applies to The Kenya Challenge mobile app and kenyachallenge.org.
      </p>

      <div className="prose prose-stone max-w-none space-y-6 text-stone-700 leading-relaxed">
        <p>
          Kenya Education Fund (KEF) is committed to respecting the privacy of every
          user of The Kenya Challenge platform &mdash; walkers, teammates, team leaders,
          supporters who pledge, and donors. We value your trust and are committed to
          being transparent and accountable when maintaining the confidentiality of
          your personal information. This Terms of Use and Privacy Policy
          (&ldquo;Policy&rdquo;) applies to all information received both online through
          the Kenya Challenge mobile app and website, and offline through any related
          communications, whether electronic, written, or oral.
        </p>

        <Section title="Health &amp; Fitness Data (Mobile App)">
          <p>
            The Kenya Challenge mobile app is a walking-based fundraiser. To
            tally your daily walking activity along a virtual route, we access
            two health-and-fitness data types from your device:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Daily step count</strong></li>
            <li><strong>Daily walking distance</strong></li>
          </ul>
          <p>On Android devices, this data is read from Google Health Connect
            using the <code>READ_STEPS</code> and <code>READ_DISTANCE</code>{' '}
            permissions, plus the standard <code>ACTIVITY_RECOGNITION</code>{' '}
            permission. On iOS devices, the same two data types are read from
            Apple&rsquo;s Core Motion framework under the &ldquo;Motion &amp;
            Fitness&rdquo; permission.
          </p>
          <p>
            We only access this data <strong>after you have explicitly granted
            the operating-system permission</strong>. If you deny or later
            revoke the permission, the app continues to work in a manual-entry
            mode.
          </p>
          <p>
            <strong>We use step and distance data solely to:</strong>
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Show your personal daily and cumulative progress along your chosen challenge route.</li>
            <li>Contribute your distance to your team&rsquo;s combined total, if you are part of a team.</li>
            <li>Allow supporters who pledged per-kilometre against your walk to see how much they will owe when the challenge ends.</li>
          </ul>
          <p>
            <strong>We do not access, collect, or infer any other Health Data.</strong>{' '}
            That includes: heart rate, sleep, calories, exercise sessions,
            workouts, weight, blood pressure, body temperature, GPS traces or
            location, or any other health, fitness, or biometric data type
            available on your device.
          </p>
          <p>
            <strong>We do not share, sell, or trade Health Data with any third
            party.</strong> We do not use Health Data for advertising or
            analytics. Health Data is transmitted only between your device and
            our authenticated server at kenyachallenge.org over HTTPS
            (TLS 1.2 or higher) and is stored in an access-controlled database.
          </p>
        </Section>

        <Section title="Data Retention">
          <p>
            We retain your personal data for as long as your account is active,
            plus a reasonable period afterwards to comply with tax-receipt,
            audit, and legal record-keeping obligations that apply to
            registered non-profits. Specifically:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Account data</strong> (name, email, display name, profile
              picture) is retained for as long as your account exists.
            </li>
            <li>
              <strong>Walking activity</strong> (daily steps, distance, and any
              cumulative totals) is retained for as long as your account exists,
              so you can review your challenge history over multiple walks.
            </li>
            <li>
              <strong>Donation and pledge records</strong> (amount, date, walker
              or team credited) are retained for at least seven (7) years after
              the transaction to comply with U.S. non-profit financial
              record-keeping requirements. This is true even after account
              deletion, but the record is anonymised (see below).
            </li>
            <li>
              <strong>System logs and email delivery records</strong> are
              retained for up to 90 days for security and troubleshooting, then
              automatically purged.
            </li>
          </ul>
        </Section>

        <Section title="Data Deletion (Your Right to Delete)">
          <p>
            You have the right to request deletion of your personal data at any
            time. There are two ways to do this:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>In-app:</strong> open the mobile app or website, sign in,
              and go to <em>Profile &rarr; Delete My Account</em>. This is also
              directly reachable at{' '}
              <a
                href="/account/delete"
                className="text-orange-600 hover:text-orange-700 underline"
              >
                kenyachallenge.org/account/delete
              </a>. Deletion is confirmed with a &ldquo;type DELETE&rdquo; step
              to prevent accidents, then executed immediately.
            </li>
            <li>
              <strong>By email:</strong> write to{' '}
              <a
                href="mailto:info@KenyaEducationFund.org"
                className="text-orange-600 hover:text-orange-700 underline"
              >
                info@KenyaEducationFund.org
              </a>{' '}
              from the email address on your account. We will confirm receipt
              within 3 business days and complete deletion within 30 days.
            </li>
          </ul>
          <p>
            <strong>What deletion removes:</strong> your login credentials,
            profile (name, display name, profile picture, email, phone if
            provided), your team membership, all Health Data we hold about you
            (steps and distance history), your activity log, and your personal
            fundraising page.
          </p>
          <p>
            <strong>What deletion preserves in anonymised form:</strong> if
            supporters have pledged to your walk or donations have been made in
            your name, the transaction records themselves are retained (per the
            Data Retention section above) but are stripped of your identifying
            information &mdash; they become anonymous line items in KEF&rsquo;s
            financial books. This is required for non-profit financial audits
            and tax-receipt integrity, and cannot be waived.
          </p>
        </Section>

        <Section title="Terms of Use">
          <p>
            The Kenya Challenge mobile app and the website at{' '}
            <strong>kenyachallenge.org</strong>, together with the linked KEF
            social media accounts, are owned and operated by KEF and are
            accessed by you (the public) under these &ldquo;Terms of Use&rdquo;.
            We ask that you read these Terms of Use before using the app,
            kenyachallenge.org, or any of the platform&rsquo;s features, including
            fundraising pages, team pages, pledging, donation processing, and
            related communications. By using the Kenya Challenge app or
            kenyachallenge.org, you accept and agree to be bound by the Terms of
            Use. If you do not agree, please exit the app or the website and do
            not access or otherwise use them. KEF reserves the right to modify
            these Terms of Use at any time. By using the app or website after
            we have modified the Terms of Use, you accept and agree to the
            modified Terms of Use.
          </p>
        </Section>

        <Section title="What We Collect">
          <p>The types of user information that we collect and maintain include:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Contact information including name, display name, email address, and (optionally) phone number and profile picture</li>
            <li>Team membership and role (walker, team leader, teammate, supporter, donor, admin)</li>
            <li>Walking activity data (see &ldquo;Health &amp; Fitness Data&rdquo; above): daily steps and daily walking distance</li>
            <li>Pledge and donation records including amount, date, and which walker or team is credited</li>
            <li>Registration-level selection and payment status (paid / unpaid), but <strong>not</strong> credit-card or bank-account numbers &mdash; see &ldquo;Security &amp; Payments&rdquo; below</li>
            <li>How you would like to be recognised on public leaderboards, team pages, and printed materials</li>
            <li>Comments, suggestions, and other information you provide voluntarily (e.g. in pledge notes or support requests)</li>
          </ul>
          <p>
            The above list is a comprehensive but not exhaustive sample. From
            time to time we may collect additional personal information as
            needed to operate the platform; when we do, this Policy will be
            updated.
          </p>
        </Section>

        <Section title="How We Use Personal Information">
          <p>KEF collects and uses your personal information to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Operate your account and personalise your experience of the app and website</li>
            <li>Show your walking progress along your chosen challenge route and update your team&rsquo;s combined total</li>
            <li>Display walkers, teams, and supporters on public leaderboards under the display name you chose</li>
            <li>Process registration fees, pledges, and donations through our payment providers (see &ldquo;Security &amp; Payments&rdquo;)</li>
            <li>Issue and deliver donation tax receipts where applicable</li>
            <li>Send transactional emails related to your account (welcome, milestones, pledge receipts, challenge completion, password reset, team notifications)</li>
            <li>Send optional newsletters &mdash; only if you opt in, and unsubscribable at any time (see &ldquo;Electronic Newsletters&rdquo;)</li>
            <li>Respond to your questions and support requests</li>
            <li>Comply with legal and regulatory obligations, including U.S. non-profit financial record-keeping</li>
          </ul>
          <p>
            In performing their duties, authorised KEF staff and administrators
            of the Kenya Challenge platform may access user personal information
            on a need-to-know basis. All persons with access are subject to
            confidentiality obligations.
          </p>
        </Section>

        <Section title="Accuracy of Content">
          <p>
            KEF makes every possible effort to ensure that our web, social media, promotional
            and written content are up to date and accurate, but KEF does not offer any
            warranties (whether express implied or otherwise) as to the reliability, accuracy or
            completeness of said content. KEF reserves the right to remove material from its
            electronic platforms, such as its website and social media accounts, without notice.
          </p>
        </Section>

        <Section title="Sharing Personal Information">
          <p>
            KEF assures users that your personal information will not be
            shared with any third party unless you have given permission or
            unless disclosure is required to operate the platform (for
            example, transmitting a pledge payment to our payment processor)
            or to comply with a legal obligation.
          </p>
          <p>
            KEF does <strong>not sell, lease, trade, share, or loan</strong>{' '}
            mailing lists of walker, supporter, or donor names or personal
            information.
          </p>
          <p>
            Publicly visible information on the Kenya Challenge platform (your
            display name, team name, walking progress, and cumulative
            fundraising totals) appears on leaderboards and fundraising pages
            by design, so that supporters can find you and cheer you on. You
            can adjust or blank out your display name from your profile at any
            time.
          </p>
        </Section>

        <Section title="16 or Under">
          <p>
            The privacy of children aged 16 or younger is very important to us.
            If you are aged 16 or under, please get your parent or guardian&rsquo;s
            permission before providing us with your personal information or
            creating a Kenya Challenge account.
          </p>
        </Section>

        <Section title="Security &amp; Payments">
          <p>
            All connections between the Kenya Challenge app or website and our
            servers use HTTPS with TLS 1.2 or higher. Passwords are stored as
            salted bcrypt hashes and are never sent in cleartext. Access to the
            backend database is restricted to a small number of authorised
            KEF administrators on a need-to-know basis, and every administrative
            action against user data is logged.
          </p>
          <p>
            Payments (registration fees, one-off donations, and pledge
            settlements) are processed by third-party payment providers:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Givebutter</strong>{' '}
              <span className="text-stone-500">(givebutter.com)</span> for
              credit and debit card donations. Givebutter is a PCI-DSS
              compliant payment platform; card and bank-account details are
              entered directly into Givebutter&rsquo;s hosted forms and are
              never transmitted through or stored on kenyachallenge.org.
            </li>
            <li>
              <strong>Safaricom M-Pesa</strong> for Kenyan mobile-money
              payments. M-Pesa handles the transaction end-to-end via its own
              secure short-code flow; we receive only the confirmation that a
              specific registration or pledge has been paid.
            </li>
          </ul>
          <p>
            <strong>We do not store your credit card number, CVV, bank
            account number, or M-Pesa PIN.</strong> We store only the fact that
            a payment succeeded, the amount, the date, and which walker or team
            is credited &mdash; the minimum required to reconcile the donation
            and issue any applicable tax receipt.
          </p>
        </Section>

        <Section title="Google Analytics">
          <p>
            We use Google Analytics to help understand and best serve our audience. For example,
            we track the total number of visitors to our site and which pages and content are
            most visited. All tracking is on an anonymous basis. To opt out of Google tracking,
            please visit their opt-out page at{' '}
            <a
              href="https://www.google.com/policies"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 hover:text-orange-700 underline"
            >
              www.google.com/policies
            </a>
            .
          </p>
        </Section>

        <Section title="Links to Other Sites">
          <p>
            We may offer links to sites that are not operated by KEF. Please note that our
            Policy does not apply to the use of these third-party websites.
          </p>
        </Section>

        <Section title="Electronic Newsletters">
          <p>
            KEF issues monthly electronic newsletters to our subscribers. If you no longer wish
            to receive KEF&rsquo;s newsletter, you may unsubscribe by clicking the
            &ldquo;unsubscribe&rdquo; link found at the bottom of the newsletter, or email{' '}
            <a
              href="mailto:info@KenyaEducationFund.org"
              className="text-orange-600 hover:text-orange-700 underline"
            >
              info@KenyaEducationFund.org
            </a>{' '}
            with your request to no longer receive our newsletter.
          </p>
        </Section>

        <Section title="Access to Information and Anonymity">
          <p>
            You are in control of the personal information we hold about you.
            You may review and update your information at any time from the
            <em> Profile</em> screen of the mobile app or website, or by writing
            to us at the email address below. If you would prefer to appear
            anonymously on public leaderboards and printed materials, you can
            blank out your display name from your profile or write to us with
            the request.
          </p>
        </Section>

        <Section title="Change in Policy">
          <p>
            We keep this Policy under regular review. We may change and update
            this Policy when necessary. When we do, we will note the last update
            at the bottom of this Policy. For changes that may be materially
            less restrictive on our use or disclosure of your personally
            identifiable information, we will obtain your consent before
            implementing the change by sending a notice.
          </p>
        </Section>

        <Section title="Contact Information">
          <p>
            To view or change the information we have on file for you, to
            request account deletion (see also &ldquo;Data Deletion&rdquo;
            above), or to remain anonymous on public materials, please send
            your request via post or email to:
          </p>
          <address className="not-italic text-stone-700">
            Kenya Education Fund<br />
            360 East 72nd Street, C3405<br />
            New York, NY 10021
          </address>
          <p>
            Email:{' '}
            <a
              href="mailto:info@KenyaEducationFund.org"
              className="text-orange-600 hover:text-orange-700 underline"
            >
              info@KenyaEducationFund.org
            </a>
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl md:text-2xl font-bold text-stone-900 mt-8">{title}</h2>
      {children}
    </section>
  );
}
